import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  Home,
  User,
  MapPin,
  Briefcase,
  Users,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Phone,
  Mail,
  ChevronRight,
  ChevronLeft,
  Globe,
  Camera,
  Upload,
  X
} from "lucide-react";
import { translations } from "@/utils/translations";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PublicApplicationForm() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [linkValid, setLinkValid] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [lang, setLang] = useState("en"); // Language state
  const [passportPhoto, setPassportPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const t = translations[lang]; // Current translations

  const [formData, setFormData] = useState({
    // Contact Info
    applicant_phone: "",
    applicant_email: "",
    mobile_phone: "",
    whatsapp_phone: "",
    // Personal Info
    full_name: "",
    age: "",
    gender: "",
    marital_status: "",
    marital_status_other: "",
    national_id: "",
    driver_license: "",
    // Residency History
    last_address_1: "",
    last_address_2: "",
    last_tenant_address: "",
    duration_at_last_location: "",
    reason_for_leaving: "",
    past_landlord_phone: "",
    can_landlord_be_contacted: "",
    // Employment
    occupation: "",
    occupation_other: "",
    employment_duration: "",
    monthly_salary: "",
    self_employed_returns: "",
    // Additional
    additional_occupants: "",
    rental_duration: "",
    // Guarantors
    guarantor_1_name: "",
    guarantor_1_phone: "",
    guarantor_1_address: "",
    guarantor_1_can_contact: "",
    guarantor_2_name: "",
    guarantor_2_phone: "",
    guarantor_2_address: "",
    guarantor_2_can_contact: "",
  });

  useEffect(() => {
    validateLink();
  }, [token]);

  const validateLink = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/public/application/${token}`);
      setLinkValid(true);
      setExpiresAt(response.data.expires_at);
      if (response.data.applicant_name) {
        setFormData(prev => ({ ...prev, full_name: response.data.applicant_name }));
      }
      if (response.data.applicant_email) {
        setFormData(prev => ({ ...prev, applicant_email: response.data.applicant_email }));
      }
    } catch (error) {
      setLinkValid(false);
      setLinkError(error.response?.data?.detail || t.linkInvalidOrExpired);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleLanguage = () => {
    setLang(prev => prev === "en" ? "yo" : "en");
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(lang === "en" ? "Please upload an image file" : "Jọ̀wọ́ fi àwòrán sílẹ̀");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(lang === "en" ? "File size must be less than 5MB" : "Ìwọ̀n fáìlì gbọ́dọ̀ kéré ju 5MB");
        return;
      }
      setPassportPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPassportPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!passportPhoto) {
          toast.error(t.photoRequired);
          return false;
        }
        if (!formData.mobile_phone && !formData.whatsapp_phone) {
          toast.error(t.contactRequired || "Please provide at least one contact number (Mobile or WhatsApp)");
          return false;
        }
        return formData.applicant_phone && formData.full_name && formData.age && 
               formData.gender && formData.marital_status && formData.national_id;
      case 2:
        return true; // Optional fields
      case 3:
        return formData.occupation && formData.additional_occupants && formData.rental_duration;
      case 4:
        return formData.guarantor_1_name && formData.guarantor_1_phone && 
               formData.guarantor_1_address && formData.guarantor_1_can_contact &&
               formData.guarantor_2_name && formData.guarantor_2_phone && 
               formData.guarantor_2_address && formData.guarantor_2_can_contact;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      toast.error(t.completeAllFields);
      return;
    }
    
    if (!passportPhoto) {
      toast.error(t.photoRequired);
      return;
    }

    setSubmitting(true);
    try {
      const submitData = new FormData();
      
      // Add all form fields
      submitData.append("full_name", formData.full_name);
      submitData.append("age", parseInt(formData.age));
      submitData.append("gender", formData.gender);
      if (formData.mobile_phone) submitData.append("mobile_phone", formData.mobile_phone);
      if (formData.whatsapp_phone) submitData.append("whatsapp_phone", formData.whatsapp_phone);
      submitData.append("marital_status", formData.marital_status);
      if (formData.marital_status_other) submitData.append("marital_status_other", formData.marital_status_other);
      submitData.append("national_id", formData.national_id);
      if (formData.driver_license) submitData.append("driver_license", formData.driver_license);
      if (formData.last_address_1) submitData.append("last_address_1", formData.last_address_1);
      if (formData.last_address_2) submitData.append("last_address_2", formData.last_address_2);
      if (formData.last_tenant_address) submitData.append("last_tenant_address", formData.last_tenant_address);
      if (formData.duration_at_last_location) submitData.append("duration_at_last_location", formData.duration_at_last_location);
      if (formData.reason_for_leaving) submitData.append("reason_for_leaving", formData.reason_for_leaving);
      if (formData.past_landlord_phone) submitData.append("past_landlord_phone", formData.past_landlord_phone);
      if (formData.can_landlord_be_contacted) submitData.append("can_landlord_be_contacted", formData.can_landlord_be_contacted);
      submitData.append("occupation", formData.occupation);
      if (formData.occupation_other) submitData.append("occupation_other", formData.occupation_other);
      if (formData.employment_duration) submitData.append("employment_duration", formData.employment_duration);
      if (formData.monthly_salary) submitData.append("monthly_salary", formData.monthly_salary);
      if (formData.self_employed_returns) submitData.append("self_employed_returns", formData.self_employed_returns);
      submitData.append("additional_occupants", formData.additional_occupants);
      submitData.append("rental_duration", formData.rental_duration);
      
      // Guarantor 1
      submitData.append("guarantor_1_name", formData.guarantor_1_name);
      submitData.append("guarantor_1_phone", formData.guarantor_1_phone);
      submitData.append("guarantor_1_address", formData.guarantor_1_address);
      submitData.append("guarantor_1_can_contact", formData.guarantor_1_can_contact);
      
      // Guarantor 2
      submitData.append("guarantor_2_name", formData.guarantor_2_name);
      submitData.append("guarantor_2_phone", formData.guarantor_2_phone);
      submitData.append("guarantor_2_address", formData.guarantor_2_address);
      submitData.append("guarantor_2_can_contact", formData.guarantor_2_can_contact);
      
      // Contact info
      if (formData.applicant_email) submitData.append("applicant_email", formData.applicant_email);
      submitData.append("applicant_phone", formData.applicant_phone);
      
      // Passport photo
      submitData.append("passport_photo", passportPhoto);

      await axios.post(`${API}/public/application/${token}`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setSubmitted(true);
      toast.success(t.applicationSubmittedSuccess);
    } catch (error) {
      toast.error(error.response?.data?.detail || t.failedToSubmit);
    } finally {
      setSubmitting(false);
    }
  };

  const formatExpiryTime = () => {
    if (!expiresAt) return "";
    try {
      const expires = new Date(expiresAt);
      const now = new Date();
      const diff = expires - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      if (hours > 0) return `${hours}h ${minutes}m ${t.timeRemaining}`;
      return `${minutes} min ${t.timeRemaining}`;
    } catch {
      return "";
    }
  };

  // Language Toggle Button Component
  const LanguageToggle = () => (
    <button
      onClick={toggleLanguage}
      className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur border border-stone-200 rounded-full shadow-sm hover:bg-white transition-colors"
      data-testid="language-toggle"
    >
      <Globe className="w-4 h-4 text-primary" />
      <span className="text-sm font-medium text-primary">
        {lang === "en" ? "Yorùbá" : "English"}
      </span>
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-text-muted">{t.validatingLink}</p>
        </div>
      </div>
    );
  }

  if (!linkValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <LanguageToggle />
        </div>
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-red-700 mb-2">{t.linkInvalidOrExpired}</h2>
            <p className="text-text-muted mb-6">{linkError}</p>
            <p className="text-sm text-stone-500">
              {t.contactAdmin}
            </p>
            <Button 
              variant="outline" 
              className="mt-6"
              onClick={() => window.location.href = "mailto:wilyrash@nilatglobal.com?subject=New Application Link Request"}
            >
              <Mail className="w-4 h-4 mr-2" />
              {t.requestNewLink}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-green-200">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-green-700 mb-2">{t.applicationSubmitted}</h2>
            <p className="text-text-muted mb-6">
              {t.thankYouMessage}
            </p>
            <p className="text-sm text-stone-500">
              {t.ifApproved}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const steps = [
    { num: 1, title: t.steps.personalInfo, icon: User },
    { num: 2, title: t.steps.residency, icon: MapPin },
    { num: 3, title: t.steps.employment, icon: Briefcase },
    { num: 4, title: t.steps.guarantors, icon: Users },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 py-8 px-4" data-testid="public-application-form">
      {/* Language Toggle - Fixed Position */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageToggle />
      </div>

      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-primary">{t.appName}</h1>
          </div>
          <h2 className="text-xl font-semibold text-stone-700">{t.formTitle}</h2>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8 overflow-x-auto pb-2">
          <div className="flex items-center gap-2">
            {steps.map((step, idx) => (
              <div key={step.num} className="flex items-center">
                <button
                  onClick={() => currentStep > step.num && setCurrentStep(step.num)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                    currentStep === step.num 
                      ? 'bg-primary text-white' 
                      : currentStep > step.num 
                        ? 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200' 
                        : 'bg-stone-200 text-stone-500'
                  }`}
                  disabled={currentStep < step.num}
                >
                  <step.icon className="w-4 h-4" />
                  <span className="hidden md:inline text-sm font-medium">{step.title}</span>
                  <span className="md:hidden text-sm font-medium">{step.num}</span>
                </button>
                {idx < steps.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-stone-400 mx-1" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <Card className="border-stone-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {(() => {
                const StepIcon = steps[currentStep - 1].icon;
                return StepIcon ? <StepIcon className="w-5 h-5 text-primary" /> : null;
              })()}
              {lang === "en" ? "Step" : "Ìgbésẹ̀"} {currentStep}: {steps[currentStep - 1].title}
            </CardTitle>
            <CardDescription>
              {t.stepDescriptions[currentStep]}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Personal Info */}
            {currentStep === 1 && (
              <div className="space-y-4">
                {/* Photo Upload Section - Top Right */}
                <div className="flex flex-col-reverse md:flex-row gap-6">
                  {/* Left Side - Contact Info */}
                  <div className="flex-1 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t.phoneNumber} {t.required}</Label>
                        <Input
                          data-testid="applicant-phone"
                          placeholder={t.enterPhoneNumber}
                          value={formData.applicant_phone}
                          onChange={(e) => handleChange("applicant_phone", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t.email}</Label>
                        <Input
                          type="email"
                          data-testid="applicant-email"
                          placeholder={t.email}
                          value={formData.applicant_email}
                          onChange={(e) => handleChange("applicant_email", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Side - Photo Upload */}
                  <div className="md:w-48 space-y-2">
                    <Label className="flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      {t.passportPhoto} {t.required}
                    </Label>
                    <div 
                      className={`relative rounded-xl border-2 border-dashed transition-colors ${
                        photoPreview 
                          ? 'border-green-400 bg-green-50' 
                          : 'border-stone-300 hover:border-primary bg-stone-50 hover:bg-stone-100'
                      }`}
                    >
                      {photoPreview ? (
                        <div className="relative">
                          <img 
                            src={photoPreview} 
                            alt="Passport preview" 
                            className="w-full h-48 object-cover rounded-lg"
                            data-testid="photo-preview"
                          />
                          <button
                            type="button"
                            onClick={removePhoto}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            data-testid="remove-photo-btn"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <div className="absolute bottom-2 left-2 right-2">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="w-full text-xs"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              {t.changePhoto}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <label 
                          className="flex flex-col items-center justify-center h-48 cursor-pointer p-4"
                          data-testid="photo-upload-area"
                        >
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                            <Upload className="w-6 h-6 text-primary" />
                          </div>
                          <span className="text-sm font-medium text-stone-700 text-center">{t.clickToUpload}</span>
                          <span className="text-xs text-stone-500 text-center mt-1">{t.dragAndDrop}</span>
                          <span className="text-xs text-stone-400 text-center mt-2">{t.supportedFormats}</span>
                        </label>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                        data-testid="photo-input"
                      />
                    </div>
                    {!passportPhoto && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {t.photoRequired}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t.fullName} {t.required}</Label>
                  <Input
                    data-testid="full-name"
                    placeholder={t.fullNamePlaceholder}
                    value={formData.full_name}
                    onChange={(e) => handleChange("full_name", e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t.age} {t.required}</Label>
                    <Input
                      type="number"
                      data-testid="age"
                      placeholder={t.age}
                      value={formData.age}
                      onChange={(e) => handleChange("age", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t.gender} {t.required}</Label>
                    <Select value={formData.gender} onValueChange={(v) => handleChange("gender", v)}>
                      <SelectTrigger data-testid="gender">
                        <SelectValue placeholder={t.select} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">{t.male}</SelectItem>
                        <SelectItem value="Female">{t.female}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Contact Info Section */}
                <div className="p-4 bg-stone-50 rounded-xl space-y-4">
                  <h3 className="font-semibold text-primary flex items-center gap-2 text-sm">
                    {t.contactInfo || "Contact Info"} ({t.atLeastOneRequired || "at least one required"})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        {t.mobilePhone || "Mobile Phone #"}
                      </Label>
                      <Input
                        data-testid="mobile-phone"
                        placeholder={t.enterPhoneNumber}
                        value={formData.mobile_phone}
                        onChange={(e) => handleChange("mobile_phone", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        {t.whatsappPhone || "WhatsApp #"} ({t.preferred || "preferred"})
                      </Label>
                      <Input
                        data-testid="whatsapp-phone"
                        placeholder={t.enterPhoneNumber}
                        value={formData.whatsapp_phone}
                        onChange={(e) => handleChange("whatsapp_phone", e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-stone-500">
                    {t.whatsappPreferred || "WhatsApp is preferred for faster communication"}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>{t.maritalStatus} {t.required}</Label>
                  <Select value={formData.marital_status} onValueChange={(v) => handleChange("marital_status", v)}>
                    <SelectTrigger data-testid="marital-status">
                      <SelectValue placeholder={t.select} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">{t.single}</SelectItem>
                      <SelectItem value="Married">{t.married}</SelectItem>
                      <SelectItem value="Divorced">{t.divorced}</SelectItem>
                      <SelectItem value="Widowed">{t.widowed}</SelectItem>
                      <SelectItem value="Other">{t.other}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.marital_status === "Other" && (
                  <div className="space-y-2">
                    <Label>{t.pleaseSpecify}</Label>
                    <Input
                      value={formData.marital_status_other}
                      onChange={(e) => handleChange("marital_status_other", e.target.value)}
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t.nationalId} {t.required}</Label>
                    <Input
                      data-testid="national-id"
                      placeholder={t.enterNIN}
                      value={formData.national_id}
                      onChange={(e) => handleChange("national_id", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t.driverLicense}</Label>
                    <Input
                      placeholder={t.enterLicenseNumber}
                      value={formData.driver_license}
                      onChange={(e) => handleChange("driver_license", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Residency History */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t.lastAddress1}</Label>
                  <Textarea
                    placeholder={t.enterPreviousAddress}
                    value={formData.last_address_1}
                    onChange={(e) => handleChange("last_address_1", e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t.lastAddress2}</Label>
                  <Textarea
                    placeholder={t.anotherPreviousAddress}
                    value={formData.last_address_2}
                    onChange={(e) => handleChange("last_address_2", e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t.lastTenantAddress}</Label>
                  <Textarea
                    placeholder={t.tenantAddressPlaceholder}
                    value={formData.last_tenant_address}
                    onChange={(e) => handleChange("last_tenant_address", e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t.durationAtLastLocation}</Label>
                    <Input
                      placeholder={t.durationPlaceholder}
                      value={formData.duration_at_last_location}
                      onChange={(e) => handleChange("duration_at_last_location", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t.pastLandlordPhone}</Label>
                    <Input
                      placeholder={t.enterPhoneNumber}
                      value={formData.past_landlord_phone}
                      onChange={(e) => handleChange("past_landlord_phone", e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t.reasonForLeaving}</Label>
                  <Textarea
                    placeholder={t.whyDidYouLeave}
                    value={formData.reason_for_leaving}
                    onChange={(e) => handleChange("reason_for_leaving", e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t.canLandlordBeContacted}</Label>
                  <Select value={formData.can_landlord_be_contacted} onValueChange={(v) => handleChange("can_landlord_be_contacted", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t.select} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">{t.yes}</SelectItem>
                      <SelectItem value="No">{t.no}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 3: Employment */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t.occupation} {t.required}</Label>
                  <Select value={formData.occupation} onValueChange={(v) => handleChange("occupation", v)}>
                    <SelectTrigger data-testid="occupation">
                      <SelectValue placeholder={t.selectOccupation} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Employed">{t.employed}</SelectItem>
                      <SelectItem value="Self-Employed">{t.selfEmployed}</SelectItem>
                      <SelectItem value="Student">{t.student}</SelectItem>
                      <SelectItem value="Retired">{t.retired}</SelectItem>
                      <SelectItem value="Unemployed">{t.unemployed}</SelectItem>
                      <SelectItem value="Other">{t.other}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.occupation === "Other" && (
                  <div className="space-y-2">
                    <Label>{t.pleaseSpecify}</Label>
                    <Input
                      value={formData.occupation_other}
                      onChange={(e) => handleChange("occupation_other", e.target.value)}
                    />
                  </div>
                )}

                {(formData.occupation === "Employed" || formData.occupation === "Self-Employed") && (
                  <>
                    <div className="space-y-2">
                      <Label>{t.durationOfEmployment}</Label>
                      <Input
                        placeholder={t.durationPlaceholder}
                        value={formData.employment_duration}
                        onChange={(e) => handleChange("employment_duration", e.target.value)}
                      />
                    </div>
                    {formData.occupation === "Employed" && (
                      <div className="space-y-2">
                        <Label>{t.monthlySalary}</Label>
                        <Input
                          placeholder={t.enterMonthlySalary}
                          value={formData.monthly_salary}
                          onChange={(e) => handleChange("monthly_salary", e.target.value)}
                        />
                      </div>
                    )}
                    {formData.occupation === "Self-Employed" && (
                      <div className="space-y-2">
                        <Label>{t.estimatedMonthlyReturns}</Label>
                        <Input
                          placeholder={t.enterEstimatedReturns}
                          value={formData.self_employed_returns}
                          onChange={(e) => handleChange("self_employed_returns", e.target.value)}
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="space-y-2">
                  <Label>{t.additionalOccupants} {t.required}</Label>
                  <Select value={formData.additional_occupants} onValueChange={(v) => handleChange("additional_occupants", v)}>
                    <SelectTrigger data-testid="additional-occupants">
                      <SelectValue placeholder={t.select} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="None">{t.livingAlone}</SelectItem>
                      <SelectItem value="1">{t.onePerson}</SelectItem>
                      <SelectItem value="2">{t.twoPeople}</SelectItem>
                      <SelectItem value="3">{t.threePeople}</SelectItem>
                      <SelectItem value="4+">{t.fourOrMore}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t.rentalDuration} {t.required}</Label>
                  <Select value={formData.rental_duration} onValueChange={(v) => handleChange("rental_duration", v)}>
                    <SelectTrigger data-testid="rental-duration">
                      <SelectValue placeholder={t.selectDuration} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6 months">{t.sixMonths}</SelectItem>
                      <SelectItem value="1 year">{t.oneYear}</SelectItem>
                      <SelectItem value="2 years">{t.twoYears}</SelectItem>
                      <SelectItem value="3+ years">{t.threeOrMoreYears}</SelectItem>
                      <SelectItem value="Undecided">{t.undecided}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 4: Guarantors */}
            {currentStep === 4 && (
              <div className="space-y-6">
                {/* Guarantor 1 */}
                <div className="p-4 bg-stone-50 rounded-xl space-y-4">
                  <h3 className="font-semibold text-primary flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {t.guarantor1}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t.guarantorName} {t.required}</Label>
                      <Input
                        data-testid="guarantor-1-name"
                        placeholder={t.enterGuarantorName}
                        value={formData.guarantor_1_name}
                        onChange={(e) => handleChange("guarantor_1_name", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t.guarantorPhone} {t.required}</Label>
                      <Input
                        data-testid="guarantor-1-phone"
                        placeholder={t.enterPhoneNumber}
                        value={formData.guarantor_1_phone}
                        onChange={(e) => handleChange("guarantor_1_phone", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t.guarantorAddress} {t.required}</Label>
                    <Textarea
                      data-testid="guarantor-1-address"
                      placeholder={t.enterGuarantorAddress}
                      value={formData.guarantor_1_address}
                      onChange={(e) => handleChange("guarantor_1_address", e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t.canGuarantorBeContacted} {t.required}</Label>
                    <Select value={formData.guarantor_1_can_contact} onValueChange={(v) => handleChange("guarantor_1_can_contact", v)}>
                      <SelectTrigger data-testid="guarantor-1-contact">
                        <SelectValue placeholder={t.select} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">{t.yes}</SelectItem>
                        <SelectItem value="No">{t.no}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Guarantor 2 */}
                <div className="p-4 bg-stone-50 rounded-xl space-y-4">
                  <h3 className="font-semibold text-primary flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {t.guarantor2}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t.guarantorName} {t.required}</Label>
                      <Input
                        data-testid="guarantor-2-name"
                        placeholder={t.enterGuarantorName}
                        value={formData.guarantor_2_name}
                        onChange={(e) => handleChange("guarantor_2_name", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t.guarantorPhone} {t.required}</Label>
                      <Input
                        data-testid="guarantor-2-phone"
                        placeholder={t.enterPhoneNumber}
                        value={formData.guarantor_2_phone}
                        onChange={(e) => handleChange("guarantor_2_phone", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t.guarantorAddress} {t.required}</Label>
                    <Textarea
                      data-testid="guarantor-2-address"
                      placeholder={t.enterGuarantorAddress}
                      value={formData.guarantor_2_address}
                      onChange={(e) => handleChange("guarantor_2_address", e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t.canGuarantorBeContacted} {t.required}</Label>
                    <Select value={formData.guarantor_2_can_contact} onValueChange={(v) => handleChange("guarantor_2_can_contact", v)}>
                      <SelectTrigger data-testid="guarantor-2-contact">
                        <SelectValue placeholder={t.select} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">{t.yes}</SelectItem>
                        <SelectItem value="No">{t.no}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(prev => prev - 1)}
                disabled={currentStep === 1}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                {t.previous}
              </Button>

              {currentStep < 4 ? (
                <Button
                  onClick={() => {
                    if (validateStep(currentStep)) {
                      setCurrentStep(prev => prev + 1);
                    } else {
                      toast.error(t.completeAllFields);
                    }
                  }}
                  className="bg-primary hover:bg-primary-800"
                  data-testid="next-step-btn"
                >
                  {t.next}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="submit-application-btn"
                >
                  {submitting ? t.submitting : t.submitApplication}
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-stone-500 mt-6">
          {t.footer}
        </p>
      </div>
    </div>
  );
}
